import React, { FC, useState } from 'react'
import { Button, Col, Divider, Icon, Row, Statistic, Table, Typography } from 'antd'
import { ColumnProps, PaginationConfig } from 'antd/lib/table'
import { ValidateConsumerUploadResponse } from '../../../../types'

interface UploadRowErrors {
	username?: string[]
	action?: string[]
}

interface UploadRow {
	row: number
	username: string
	action: 'add' | 'delete'
	errors?: UploadRowErrors
}

interface UploadConsumersProps {
	data: ValidateConsumerUploadResponse
	resetData: () => void
	uploadDataHandler: () => Promise<void>
}

const ValidateUpload: FC<UploadConsumersProps> = ({ data, resetData, uploadDataHandler }) => {
	const [isUploading, setIsUploading] = useState<boolean>(false)
	const hasFileFailedValidation = data?.metadata?.validation?.failed > 0
	const isLicenseQuotaExceeded = data.metadata.totals.consumedLicensesAfterChanges > data.metadata.totals.licenseTotal
	const { Text } = Typography

	const startUpload = async () => {
		setIsUploading(true)
		await uploadDataHandler()
	}

	const tableColumns: ColumnProps<UploadRow>[] = [
		{
			render: (_, record) => {
				return record.errors ? <Icon type='close' style={{ color: '#cf1322' }} /> : <Icon type='check' style={{ color: '#3f8600' }} />
			}
		},
		{
			title: 'Row',
			dataIndex: 'row'
		},
		{
			title: 'Username',
			dataIndex: 'username'
		},
		{
			title: 'Action',
			dataIndex: 'action',
			render: action => {
				if (action === 'add') {
					return <>Add</>
				}

				if (action === 'delete') {
					return <>Delete</>
				}

				return action
			}
		}
	]

	const paginationConfig: PaginationConfig = {
		hideOnSinglePage: true,
		pageSize: 10
	}

	return (
		<>
			<h2>File upload</h2>
			<Row type='flex' justify='space-between' gutter={20} style={{ marginBottom: 20 }}>
				<Col>
					<Statistic
						title='Passed'
						valueRender={val => <span style={{ color: '#3f8600' }}>{val}</span>}
						value={data.metadata.validation.passed}
						suffix={`/ ${data.metadata.validation.total}`}
					/>
				</Col>
				<Col>
					<Statistic
						title='Failed'
						valueRender={val => <span style={{ color: '#cf1322' }}>{val}</span>}
						value={data.metadata.validation.failed}
						suffix={`/ ${data.metadata.validation.total}`}
					/>
				</Col>
				<Col>
					<Statistic title='Additions' value={data.metadata.changeCount.additions} />
				</Col>
				<Col>
					<Statistic title='Deletions' value={data.metadata.changeCount.deletions} />
				</Col>
				<Col>
					<Statistic
						title='Licenses Used'
						value={data.metadata.totals.consumedLicensesAfterChanges}
						valueRender={val => {
							return isLicenseQuotaExceeded ? <span style={{ color: '#cf1322' }}>{val}</span> : <span style={{ color: '#3f8600' }}>{val}</span>
						}}
						suffix={`/ ${data.metadata.totals.licenseTotal}`}
					/>
				</Col>
			</Row>

			{!hasFileFailedValidation ? (
				<>
					<h3>
						<Icon type='check' style={{ color: '#3f8600' }} /> File passed validation
					</h3>
					<p>Please double check you are happy with the below table and then click upload to proceed.</p>
				</>
			) : (
				<>
					<h3>
						<Icon type='close' style={{ color: '#cf1322' }} /> File failed validation
					</h3>
					<p>There were some issues with the file you provided. Please check the below table, make the necessary amendments and reupload.</p>
				</>
			)}

			{isLicenseQuotaExceeded && (
				<>
					{' '}
					<h3>
						<Icon type='close' style={{ color: '#cf1322' }} /> You do not have enough licenses
					</h3>
					<p>Please remove some consumers or increase your number of available licenses to proceed.</p>{' '}
				</>
			)}

			{hasFileFailedValidation && (
				<div style={{ marginBottom: 15 }}>
					<Table
						tableLayout='unset'
						size='small'
						columns={[
							...tableColumns,
							{
								title: 'Errors',
								dataIndex: 'errors',
								render: (errors: UploadRowErrors) => {
									const errorsObject = Object.values(errors).flat()
									return (
										<ul style={{ listStyleType: 'none', padding: 0, marginBottom: 0 }}>
											{errorsObject
												.filter((error, index) => {
													return errorsObject.indexOf(error) === index
												})
												.map((error: string) => (
													<li key={error}>
														<Text style={{ color: '#cf1322' }}>
															<Icon type='close' /> {error}
														</Text>
													</li>
												))}
										</ul>
									)
								}
							}
						]}
						dataSource={data.failed}
						pagination={paginationConfig}
					/>
				</div>
			)}
			{data?.passed?.length > 0 && (
				<Table tableLayout='unset' size='small' columns={tableColumns} dataSource={data.passed} pagination={paginationConfig} />
			)}
			<Divider />
			<Row type='flex' align='middle' justify='space-between' style={{ marginTop: 15 }}>
				<Button size='large' icon='left' onClick={() => resetData()}>
					Go back
				</Button>
				<Button disabled={hasFileFailedValidation} onClick={() => startUpload()} loading={isUploading} icon='upload' type='primary' size='large'>
					Upload
				</Button>
			</Row>
		</>
	)
}

export default ValidateUpload
